import {
  addDays,
  addMinutes,
  differenceInHours,
  differenceInMinutes,
  format,
  isAfter,
  isBefore,
  subDays,
} from 'date-fns';
import { dbDayDateToken } from '../../static/dbDayDateToken';
import { Jobs } from '../collections/jobs';
import { Job } from '../models/job';
import { InviteeStatusEnum } from '../models/jobInviteeStatusEnum';
import { Shift } from '../models/shift';
import { PoolerUser } from '../models/user';
import { UserRestWarningTypeEnum } from '../models/userRestWarning';
import { getJobEnd, getJobStart } from './getJobStart';
import {
  militaryTimeToHourAndMinuteStrings,
  militaryTimeToMinutes,
} from './time';

export type RestedResult = {
  hasRested: boolean;
  restType?: UserRestWarningTypeEnum;
  withBreakInfraction?: boolean;
  workedUntil?: string;
  startsWorkingAt?: string;
};

export function checkIfRested(options: {
  user: PoolerUser;
  jobDate: string;
  jobShifts: Shift[];
  requiredPause?: number;
  availabilitiesByDate?: Map<string, any>;
  currentJobId?: string;
  blockingOnly?: boolean;
  excludeJobIds?: string[];
}): RestedResult {
  const result: RestedResult = { hasRested: true };
  if (!options.user.availabilities) {
    return { hasRested: true };
  }

  if (!options.requiredPause) {
    options.requiredPause = 11;
  }

  let jobStart = getJobStart(options.jobShifts, options.jobDate);
  let jobEnd = getJobEnd(options.jobShifts, options.jobDate);

  options.user.restWarnings
    ?.filter(
      (restWarning) =>
        (restWarning.affectedDates.includes(options.jobDate) &&
          restWarning.type === UserRestWarningTypeEnum.ELEVEN_HOUR_WARNING &&
          restWarning.bookedJob?.jobId !== options.currentJobId &&
          !options.excludeJobIds?.includes(restWarning.bookedJob?.jobId)) ||
        (restWarning.type === UserRestWarningTypeEnum.SEVEN_DAY_WARNING &&
          !restWarning.bookedJobs?.some(
            (bookedJob) =>
              bookedJob.jobId === options.currentJobId ||
              options.excludeJobIds?.includes(bookedJob.jobId),
          )) ||
        (restWarning.type ===
          UserRestWarningTypeEnum.POTENTIAL_SEVEN_DAY_WARNING &&
          restWarning.affectedDates.includes(options.jobDate)),
    )
    .forEach((restWarning) => {
      if (restWarning.type === UserRestWarningTypeEnum.ELEVEN_HOUR_WARNING) {
        let bookedFrom = new Date(restWarning.bookedJob.bookedFrom);
        let bookedUntil = new Date(restWarning.bookedJob.bookedUntil);

        let jobBeforeRestWarning = isBefore(jobEnd, bookedFrom);
        let jobAfterRestWarning = isAfter(jobStart, bookedUntil);

        if (
          jobAfterRestWarning &&
          differenceInMinutes(jobStart, bookedUntil) / 60 <
            (options.requiredPause || 11)
        ) {
          result.hasRested = false;
          result.restType = restWarning.type;
          result.workedUntil = format(bookedUntil, 'dd.MM.yyyy HH:mm');
          return;
        } else if (
          jobBeforeRestWarning &&
          differenceInMinutes(bookedFrom, jobEnd) / 60 <
            (options.requiredPause || 11)
        ) {
          result.hasRested = false;
          result.restType = restWarning.type;
          result.startsWorkingAt = format(bookedFrom, 'dd.MM.yyyy HH:mm');
          return;
        } else if (!jobBeforeRestWarning && !jobAfterRestWarning) {
          // Jobs overlap - show start time if job starts before booked job starts,
          // otherwise show end time
          result.hasRested = false;
          result.restType = restWarning.type;
          if (isBefore(jobStart, bookedFrom)) {
            result.startsWorkingAt = format(bookedFrom, 'dd.MM.yyyy HH:mm');
          } else {
            result.workedUntil = format(bookedUntil, 'dd.MM.yyyy HH:mm');
          }
          return;
        }
      } else if (
        !options.blockingOnly &&
        restWarning.type === UserRestWarningTypeEnum.SEVEN_DAY_WARNING
      ) {
        let sevenDayWorkStart = new Date(restWarning.sevenDayWorkStart);
        let sevenDayWorkEnd = new Date(restWarning.sevenDayWorkEnd);
        let jobBeforeRestWarning = isBefore(jobEnd, sevenDayWorkStart);
        let jobAfterRestWarning = isAfter(jobStart, sevenDayWorkEnd);

        if (
          jobAfterRestWarning &&
          differenceInHours(jobStart, restWarning.sevenDayWorkEnd) < 83
        ) {
          result.hasRested = false;
          result.restType = restWarning.type;
          result.workedUntil = format(sevenDayWorkEnd, 'dd.MM.yyyy HH:mm');
          return;
        } else if (
          jobBeforeRestWarning &&
          differenceInHours(restWarning.sevenDayWorkStart, jobEnd) < 24
        ) {
          result.hasRested = false;
          result.restType = restWarning.type;
          result.startsWorkingAt = format(
            sevenDayWorkStart,
            'dd.MM.yyyy HH:mm',
          );
          return;
        } else if (!jobBeforeRestWarning && !jobAfterRestWarning) {
          result.hasRested = false;
          result.restType = restWarning.type;
          return;
        }
      } else if (
        !options.blockingOnly &&
        restWarning.type === UserRestWarningTypeEnum.POTENTIAL_SEVEN_DAY_WARNING
      ) {
        if (restWarning.affectedDates.includes(options.jobDate)) {
          result.hasRested = false;
          result.restType = restWarning.type;
          result.withBreakInfraction = restWarning.withBreakInfraction;
          return;
        }
      }
    });

  return result;
}

export function isCurrentlyEmployed(
  user: PoolerUser,
  customerId: string,
  jobDate: string,
): Boolean {
  if (!user.availabilities) {
    return false;
  }
  const joinDate = user.customerInformation?.[customerId]?.joinDate
    ? format(
        new Date(user.customerInformation[customerId].joinDate!),
        dbDayDateToken,
      )
    : undefined;
  const leaveDate = user.customerInformation?.[customerId]?.leaveDate
    ? format(
        new Date(user.customerInformation[customerId].leaveDate!),
        dbDayDateToken,
      )
    : undefined;
  if (joinDate && leaveDate && joinDate > leaveDate) {
    //  add one day to leave date to allow for booking until midnight of that day
    return jobDate < leaveDate + 86399 || jobDate >= joinDate;
  }
  return !(
    (leaveDate && jobDate > leaveDate) ||
    (joinDate && jobDate < joinDate)
  );
}

export function getFirstShift(shifts: Shift[]): Shift {
  return shifts.reduce((previousValue, currentValue) =>
    currentValue.shift < previousValue.shift ? currentValue : previousValue,
  );
}

export function getLastShift(shifts: Shift[]): Shift {
  return shifts.reduce((previousValue, currentValue) =>
    currentValue.shift > previousValue.shift ? currentValue : previousValue,
  );
}

export function getJobFromAsDate(date: string, from: number): Date {
  return addMinutes(new Date(date), militaryTimeToMinutes(from.toString()));
}

export function getJobFromAsUTCDate(date: string, from: number): Date {
  let baseDate = new Date(date);
  let hoursAndMinutes = militaryTimeToHourAndMinuteStrings(from);

  let newDateString = `${format(baseDate, dbDayDateToken)}T${
    hoursAndMinutes.hours
  }:${hoursAndMinutes.minutes}`;

  let localDate = new Date(newDateString);
  let utcDate = new Date(
    localDate.getTime() - localDate.getTimezoneOffset() * 60000,
  );

  return utcDate;
}

export function getJobToAsDate(date: string, from: number, to: number): Date {
  let baseDate = new Date(date);
  if (to < from) {
    baseDate = addDays(baseDate, 1);
  }
  let hoursAndMinutes = militaryTimeToHourAndMinuteStrings(to);

  let newDateString = `${format(baseDate, dbDayDateToken)}T${
    hoursAndMinutes.hours
  }:${hoursAndMinutes.minutes}`;

  let localDate = new Date(newDateString);
  let utcDate = new Date(
    localDate.getTime() - localDate.getTimezoneOffset() * 60000,
  );

  return utcDate;
}

export function hasOtherOverlappingJobs(
  userId: string,
  job: Job,
  excludeJobIds?: string[],
): boolean {
  if (!userId) return false;

  const jobStartTime = job.shifts[0].from;
  const jobEndTime = job.shifts[job.shifts.length - 1].to;
  const jobGoesOvernight = jobStartTime > jobEndTime;

  const jobDates = {
    dateBefore: format(subDays(new Date(job.date), 1), dbDayDateToken),
    date: job.date,
    dateAfter: format(addDays(new Date(job.date), 1), dbDayDateToken),
  };

  const excluded = [job._id, ...(excludeJobIds || [])].filter(
    (id): id is string => !!id,
  );

  let query: Mongo.Selector<Job> = {
    _id: { $nin: excluded },
    invitees: {
      $elemMatch: { userId: userId, status: InviteeStatusEnum.BOOKED },
    },
    $or: [],
  };

  // Check for same shift overlaps
  query['$or']!.push({
    date: jobDates.date,
    'shifts.shift': { $in: job.shifts.map((shift) => shift.shift) },
  });

  if (jobGoesOvernight) {
    // Current job goes overnight (e.g., 22:00 to 06:00)
    query['$or']!.push(
      ...[
        // Other job starts after our start time (same day)
        { 'shifts.from': { $gte: jobStartTime }, date: jobDates.date },
        // Other job ends after our start time (same day)
        { 'shifts.to': { $gte: jobStartTime }, date: jobDates.date },
        // Other job start before our end time (next day)
        { 'shifts.from': { $lte: jobEndTime }, date: jobDates.dateAfter },
        // Other job wraps our job over night
        {
          date: jobDates.date,
          $expr: {
            $gt: ['$shifts.from', '$shifts.to'],
          },
        },
      ],
    );
  } else {
    query['$or']!.push({
      // other job is not overnight
      date: jobDates.date,
      $or: [
        // starts in our job
        { 'shifts.from': { $gte: jobStartTime, $lte: jobEndTime } },
        // ends in our job
        { 'shifts.to': { $gte: jobStartTime, $lte: jobEndTime } },
        // wrapping our job
        {
          'shifts.from': { $lte: jobStartTime },
          'shifts.to': { $gte: jobEndTime },
        },
      ],
      // expression last for performance
      $expr: {
        $gt: ['$shifts.to', '$shifts.from'],
      },
    });
    query['$or']!.push({
      // other job is overnight
      $or: [
        // yesterday with to after our from
        { 'shifts.to': { $gte: jobStartTime }, date: jobDates.dateBefore },
        // today with from before our to
        { 'shifts.from': { $lte: jobEndTime }, date: jobDates.date },
      ],
      // expression last for performance
      $expr: {
        $gt: ['$shifts.from', '$shifts.to'],
      },
    });
  }

  let overlappingJobs = Jobs.find(query).fetch();

  return overlappingJobs.length > 0;
}
